import { useEffect, useCallback } from 'react'
import toast from 'react-hot-toast'

/**
 * Hook for managing browser notifications
 */
export function useNotifications() {
  // Request permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      // Don't auto-request, let user enable in settings
    }
  }, [])

  /**
   * Send a notification
   */
  const sendNotification = useCallback((title, options = {}) => {
    // Always show toast
    toast.success(options.body || title)

    // Also send browser notification if permitted
    if ('Notification' in window && Notification.permission === 'granted') {
      try {
        new Notification(title, {
          icon: '/vite.svg',
          badge: '/vite.svg',
          tag: options.tag || 'task-reminder',
          requireInteraction: options.requireInteraction || false,
          ...options,
        })
      } catch (error) {
        console.error('Failed to send notification:', error)
      }
    }
  }, [])

  /**
   * Check if notifications are supported and permitted
   */
  const getNotificationStatus = useCallback(() => {
    if (!('Notification' in window)) {
      return 'unsupported'
    }
    return Notification.permission
  }, [])

  /**
   * Request notification permission
   */
  const requestPermission = useCallback(async () => {
    if (!('Notification' in window)) {
      return 'unsupported'
    }

    try {
      const permission = await Notification.requestPermission()
      return permission
    } catch (error) {
      console.error('Failed to request permission:', error)
      return 'denied'
    }
  }, [])

  return {
    sendNotification,
    getNotificationStatus,
    requestPermission,
  }
}

/**
 * Hook for polling reminders from the server
 */
export function useReminderPolling(interval = 60000) {
  const { sendNotification } = useNotifications()

  useEffect(() => {
    const checkReminders = async () => {
      try {
        const response = await fetch('/api/reminders/due')
        const data = await response.json()

        if (data.reminders && data.reminders.length > 0) {
          data.reminders.forEach(reminder => {
            sendNotification('⏰ Task Reminder', {
              body: reminder.taskTitle,
              tag: `reminder-${reminder.id}`,
              requireInteraction: true,
            })
          })
        }
      } catch (error) {
        console.error('Failed to check reminders:', error)
      }
    }

    // Check immediately
    checkReminders()

    // Then poll periodically
    const pollInterval = setInterval(checkReminders, interval)

    return () => clearInterval(pollInterval)
  }, [interval, sendNotification])
}